import React, { useState, useEffect, useCallback } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Mail, Send, Plus, Trash2, RefreshCw, Users, CheckCircle, XCircle, Clock, Eye, UserPlus, Bell, Palette, Music, Volume2, VolumeX } from '../icons';
import { useAuth } from '../context/AuthContext';
import { useLanguage } from '../context/LanguageContext';
import InvitationCardEditor from '../components/InvitationCardEditor';
import { useConfirm } from '../components/Modal';
import { onRsvpUpdate } from '../services/socket';
import { toast } from 'react-toastify';
import mediaUrl from '../utils/mediaUrl';
import {
  getWeddings, getGuests, getInvitations, getInvitationStats,
  createInvitation, createBulkInvitations, sendInvitation, sendBulkInvitations,
  resendInvitation, updateInvitation, deleteInvitation,
  updateReminderFrequency, updateBulkReminderFrequency,
  uploadInvitationAudio, removeInvitationAudio
} from '../services/api';

function Invitations() {
  const { user, loading: authLoading } = useAuth();
  const { t } = useLanguage();
  const navigate = useNavigate();
  const [confirm, ConfirmDialog] = useConfirm();
  const [wedding, setWedding] = useState(null);
  const [invitations, setInvitations] = useState([]);
  const [stats, setStats] = useState({});
  const [guests, setGuests] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('all');
  const [showAddForm, setShowAddForm] = useState(false);
  const [showBulkImport, setShowBulkImport] = useState(false);
  const [selectedGuests, setSelectedGuests] = useState([]);
  const [bulkMessage, setBulkMessage] = useState('');
  const [sending, setSending] = useState({});
  const [editingId, setEditingId] = useState(null);
  const [editForm, setEditForm] = useState({});
  const [newInvite, setNewInvite] = useState({ recipient_name: '', recipient_email: '', recipient_phone: '', message: '', group_name: 'General' });
  const [filterStatus, setFilterStatus] = useState('all');
  const [filterBulkGroup, setFilterBulkGroup] = useState('all');
  const [showCardEditor, setShowCardEditor] = useState(false);
  const [editingCardId, setEditingCardId] = useState(null);
  const [audioUrl, setAudioUrl] = useState(null);
  const [audioUploading, setAudioUploading] = useState(false);
  const audioInputRef = React.useRef(null);

  const loadData = useCallback(async () => {
    if (!wedding) return;
    try {
      const [invRes, statsRes, guestRes] = await Promise.all([
        getInvitations(wedding.id),
        getInvitationStats(wedding.id),
        getGuests(wedding.id),
      ]);
      setInvitations(invRes.data);
      setStats(statsRes.data);
      setGuests(guestRes.data.guests || []);
      // Sync audio URL from first invitation that has one
      const withAudio = invRes.data.find(i => i.audio_url);
      setAudioUrl(withAudio?.audio_url || null);
    } catch {
      toast.error(t('invitations.loadFailed'), { toastId: 'invitations-load-failed' });
    }
  }, [wedding, t]);

  useEffect(() => {
    if (authLoading) return;
    if (!user) { navigate('/login'); return; }
    if (user.role !== 'couple' && user.role !== 'admin') { navigate('/dashboard'); return; }
    getWeddings()
      .then(res => {
        if (res.data.length > 0) setWedding(res.data[0]);
        else setLoading(false);
      })
      .catch(() => setLoading(false));
  }, [user, navigate, authLoading]);

  useEffect(() => {
    if (wedding) {
      loadData().finally(() => setLoading(false));
    }
  }, [wedding, loadData]);

  // Real-time RSVP updates
  useEffect(() => {
    const unsub = onRsvpUpdate(() => { loadData(); });
    return unsub;
  }, [loadData]);

  const handleCreateInvite = async (e) => {
    e.preventDefault();
    if (!newInvite.recipient_name || !newInvite.recipient_email) return;
    const emailLower = newInvite.recipient_email.trim().toLowerCase();
    const existing = invitations.find(i => i.recipient_email?.toLowerCase() === emailLower);
    if (existing) {
      toast.error(`An invitation has already been sent to this email (Status: ${existing.email_status || 'pending'})`);
      return;
    }
    try {
      await createInvitation(wedding.id, newInvite);
      setNewInvite({ recipient_name: '', recipient_email: '', recipient_phone: '', message: '', group_name: 'General' });
      setShowAddForm(false);
      toast.success('Invitation created');
      await loadData();
    } catch (err) {
      toast.error(err.response?.data?.error || 'Failed to create invitation');
    }
  };

  const handleBulkImport = async () => {
    if (selectedGuests.length === 0) return;
    try {
      const res = await createBulkInvitations(wedding.id, { guest_ids: selectedGuests, message: bulkMessage });
      // Imports are drafts: say nothing was emailed yet, and name anyone skipped
      const created = res.data.created || 0;
      const skipped = res.data.skipped || [];
      const reasonText = { no_email: t('invitations.skipNoEmail'), already_invited: t('invitations.skipAlreadyInvited'), not_found: t('invitations.skipNotFound') };
      let summary = created > 0 ? t('invitations.importedSummary').replace('{count}', created) : t('invitations.importedNone');
      if (skipped.length > 0) {
        const names = skipped.map(s => `${s.name || '#' + s.guest_id} (${reasonText[s.reason] || s.reason})`).join(', ');
        summary += ` ${t('invitations.skippedSummary').replace('{count}', skipped.length)} ${names}`;
      }
      (skipped.length > 0 || created === 0 ? toast.warn : toast.success)(summary);
      setShowBulkImport(false);
      setSelectedGuests([]);
      setBulkMessage('');
      await loadData();
    } catch (err) {
      toast.error(err.response?.data?.error || 'Failed to import guests');
    }
  };

  const handleSend = async (id) => {
    setSending(prev => ({ ...prev, [id]: true }));
    try {
      const res = await sendInvitation(id);
      toast.success('Invitation sent');
      if (res.data?.sms === 'disabled') toast.info(t('invitations.smsDisabled'));
      await loadData();
    } catch (err) {
      toast.error(err.response?.data?.error || 'Failed to send');
    }
    setSending(prev => ({ ...prev, [id]: false }));
  };

  const handleResend = async (id) => {
    setSending(prev => ({ ...prev, [id]: true }));
    try {
      const res = await resendInvitation(id);
      toast.success('Invitation resent');
      if (res.data?.sms === 'disabled') toast.info(t('invitations.smsDisabled'));
      await loadData();
    } catch (err) {
      toast.error(err.response?.data?.error || 'Failed to resend');
      await loadData();
    }
    setSending(prev => ({ ...prev, [id]: false }));
  };

  // Send-all only picks up drafts, so failed ones get their own retry
  const handleRetryFailed = async () => {
    const failedInvs = invitations.filter(i => i.email_status === 'failed');
    if (failedInvs.length === 0) return;
    setSending(prev => ({ ...prev, retry: true }));
    const stillFailing = [];
    for (const inv of failedInvs) {
      try {
        await resendInvitation(inv.id);
      } catch (err) {
        stillFailing.push(`${inv.recipient_email} (${err.response?.data?.error || err.message})`);
      }
    }
    const sentCount = failedInvs.length - stillFailing.length;
    if (stillFailing.length > 0) toast.warn(`Sent: ${sentCount} · ${t('invitations.sendFailures')} ${stillFailing.join(', ')}`);
    else toast.success(`Sent: ${sentCount}`);
    await loadData();
    setSending(prev => ({ ...prev, retry: false }));
  };

  const handleSendAllDrafts = async () => {
    const ok = await confirm({ title: 'Send All Drafts', message: `Send all ${stats.draft || 0} draft invitations?`, confirmText: 'Send All' });
    if (!ok) return;
    setSending(prev => ({ ...prev, bulk: true }));
    try {
      const res = await sendBulkInvitations(wedding.id);
      const tone = res.data.failed > 0 ? toast.warn : toast.success;
      // Name the failures (and why) so the couple knows which addresses to fix
      const failures = (res.data.failures || []).map(f => `${f.email} (${f.error})`).join(', ');
      tone(`Sent: ${res.data.sent}${res.data.failed > 0 ? ` · ${t('invitations.sendFailures')} ${failures || res.data.failed}` : ''}`);
      if (res.data.sms_disabled > 0) toast.info(t('invitations.smsDisabledBulk').replace('{count}', res.data.sms_disabled));
      await loadData();
    } catch (err) {
      toast.error(err.response?.data?.error || 'Failed to send');
    }
    setSending(prev => ({ ...prev, bulk: false }));
  };

  const handleDelete = async (inv) => {
    // The server also deletes the linked guest (invitations.js DELETE), so warn about it
    const message = inv.guest_id
      ? t('invitations.deleteConfirmGuest').replace('{name}', inv.recipient_name)
      : t('invitations.deleteConfirm');
    const ok = await confirm({ title: t('invitations.deleteInvitation'), message, danger: true, confirmText: t('common.delete') });
    if (!ok) return;
    try {
      await deleteInvitation(inv.id);
      await loadData();
    } catch (err) {
      toast.error(err.response?.data?.error || t('invitations.deleteFailed'));
    }
  };

  const handleEditSave = async (id) => {
    try {
      await updateInvitation(id, editForm);
      setEditingId(null);
      await loadData();
    } catch (err) {
      toast.error(err.response?.data?.error || t('invitations.saveFailed'));
    }
  };

  const handleAudioUpload = async (e) => {
    const file = e.target.files?.[0];
    // Clear the input so picking the same file again after an error still fires onChange
    e.target.value = '';
    if (!file || !wedding) return;
    if (file.size > 20 * 1024 * 1024) { toast.error(t('invitations.audioTooLarge')); return; }
    setAudioUploading(true);
    try {
      const res = await uploadInvitationAudio(wedding.id, file);
      const baseUrl = (process.env.REACT_APP_API_URL || '').replace('/api', '');
      const url = res.data.url;
      setAudioUrl(url.startsWith('/') ? baseUrl + url : url);
      toast.success('Audio message attached to all invitations');
    } catch (err) {
      toast.error(err.response?.data?.error || t('invitations.audioUploadFailed'));
    } finally {
      setAudioUploading(false);
    }
  };

  const handleAudioRemove = async () => {
    if (!wedding) return;
    try {
      await removeInvitationAudio(wedding.id);
      setAudioUrl(null);
      toast.success('Audio message removed');
    } catch {
      toast.error('Failed to remove audio');
    }
  };

  const toggleGuestSelect = (guestId) => {
    setSelectedGuests(prev =>
      prev.includes(guestId) ? prev.filter(id => id !== guestId) : [...prev, guestId]
    );
  };

  // Case-insensitive, matching the server's duplicate check
  const findInvitation = (email) => invitations.find(i => (i.recipient_email || '').toLowerCase() === (email || '').toLowerCase());
  // Same rule as the Guest List's "Awaiting reply": an email actually went out at some point
  const wasSent = (inv) => !!inv.sent_at || inv.email_status === 'sent' || inv.email_status === 'delivered';
  const failedCount = invitations.filter(i => i.email_status === 'failed').length;
  const guestsWithoutEmail = guests.filter(g => !g.email).length;

  const selectAllGuests = () => {
    const eligible = guests.filter(g => g.email && !findInvitation(g.email) && (filterBulkGroup === 'all' || g.group_name === filterBulkGroup));
    if (selectedGuests.length === eligible.length) setSelectedGuests([]);
    else setSelectedGuests(eligible.map(g => g.id));
  };

  const filteredInvitations = invitations.filter(inv => {
    if (filterStatus === 'all') return true;
    if (filterStatus === 'draft') return inv.email_status === 'draft';
    if (filterStatus === 'sent') return inv.email_status === 'sent' || inv.email_status === 'delivered';
    if (filterStatus === 'failed') return inv.email_status === 'failed';
    if (filterStatus === 'accepted') return inv.rsvp_status === 'accepted';
    if (filterStatus === 'declined') return inv.rsvp_status === 'declined';
    if (filterStatus === 'pending') return inv.rsvp_status === 'pending' && wasSent(inv);
    return true;
  });

  const rsvpBadge = (status) => {
    const colors = { accepted: '#22C55E', declined: '#EF4444', pending: '#F59E0B', maybe: '#3B82F6' };
    const tooltips = {
      accepted: 'Guest confirmed they will attend',
      declined: 'Guest declined the invitation',
      pending: 'Waiting for guest to respond',
      maybe: 'Guest may attend — no firm commitment',
    };
    return (
      <span title={tooltips[status]} style={{
        display: 'inline-block', padding: '2px 10px', borderRadius: '999px', fontSize: '11px', fontWeight: 600,
        background: `${colors[status]}20`, color: colors[status], cursor: 'default',
      }}>
        {status === 'accepted' && <CheckCircle size={11} style={{ verticalAlign: 'middle', marginRight: '3px' }} />}
        {status === 'declined' && <XCircle size={11} style={{ verticalAlign: 'middle', marginRight: '3px' }} />}
        {status === 'pending' && <Clock size={11} style={{ verticalAlign: 'middle', marginRight: '3px' }} />}
        {/* Same wording as the Guest List badges */}
        {{ accepted: t('guestList.accepted'), declined: t('guestList.declined'), pending: t('guestList.awaitingReply'), maybe: t('guestList.maybe') }[status] || status}
      </span>
    );
  };

  const emailBadge = (status) => {
    const colors = { draft: '#6B7280', sent: '#3B82F6', delivered: '#22C55E', failed: '#EF4444' };
    const tooltips = {
      draft: 'Not yet sent — click Send to deliver',
      sent: 'Invitation email sent to guest',
      delivered: 'Email confirmed delivered',
      failed: t('invitations.failedHint'),
    };
    const labels = { draft: t('invitations.emailDraft'), sent: t('invitations.emailSent'), delivered: t('invitations.emailDelivered'), failed: t('invitations.emailFailed') };
    return (
      <span title={tooltips[status]} style={{
        display: 'inline-block', padding: '2px 10px', borderRadius: '999px', fontSize: '11px', fontWeight: 600,
        background: `${colors[status]}20`, color: colors[status], cursor: 'default',
      }}>
        {labels[status] || status}
      </span>
    );
  };

  if (loading) return <div className="loading-page"><div className="spinner"></div></div>;

  if (!wedding) {
    return (
      <div className="container" style={{ padding: '60px 20px', textAlign: 'center' }}>
        <Mail size={48} style={{ color: '#ccc', marginBottom: '16px' }} />
        <h2>{t('invitations.noWeddingFound')}</h2>
        <p style={{ color: '#6b7280' }}>{t('invitations.createWeddingFirst')}</p>
        <button className="btn btn-primary" onClick={() => navigate('/dashboard')} style={{ marginTop: '16px' }}>{t('invitations.goToDashboard')}</button>
      </div>
    );
  }

  return (
    <div className="container" style={{ padding: '30px 20px', maxWidth: '1100px', margin: '0 auto' }}>
      {/* Header */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', flexWrap: 'wrap', gap: '16px', marginBottom: '24px' }}>
        <div>
          <h1 style={{ margin: 0, display: 'flex', alignItems: 'center', gap: '10px' }}>
            <Mail size={28} style={{ color: '#be185d' }} /> {t('invitations.title')}
          </h1>
          <p style={{ color: '#6b7280', margin: '4px 0 0' }}>
            {t('invitations.subtitle')}
          </p>
        </div>
        <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
          <button className="btn btn-outline" onClick={() => { setShowCardEditor(!showCardEditor); setEditingCardId(null); }} style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
            <Palette size={16} /> {showCardEditor ? t('invitations.closeDesigner') : t('invitations.designCard')}
          </button>
          <button className="btn btn-outline" onClick={() => setShowBulkImport(!showBulkImport)} style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
            <UserPlus size={16} /> {t('invitations.importFromGuests')}
          </button>
          <button className="btn btn-primary" onClick={() => setShowAddForm(!showAddForm)} style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
            <Plus size={16} /> {t('invitations.newInvitation')}
          </button>
        </div>
      </div>

      {/* Audio Message Panel */}
      <div style={{ background: '#fff', border: '1px solid #e5e7eb', borderRadius: '12px', padding: '16px 20px', marginBottom: '20px', display: 'flex', alignItems: 'center', gap: '16px', flexWrap: 'wrap' }}>
        <Music size={20} style={{ color: '#be185d', flexShrink: 0 }} />
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{ fontWeight: 600, fontSize: '14px', color: '#374151' }}>Audio Message</div>
          <div style={{ fontSize: '12px', color: '#6b7280' }}>Attach a voice/audio message — guests can play it on the invitation page</div>
          {audioUrl && (
            <audio controls src={mediaUrl(audioUrl)} style={{ marginTop: '8px', width: '100%', maxWidth: '360px', height: '36px' }} />
          )}
        </div>
        <div style={{ display: 'flex', gap: '8px', flexShrink: 0 }}>
          <button className="btn btn-outline" onClick={() => audioInputRef.current?.click()} disabled={audioUploading} style={{ display: 'flex', alignItems: 'center', gap: '6px', fontSize: '13px' }}>
            <Volume2 size={14} /> {audioUploading ? 'Uploading…' : audioUrl ? 'Replace Audio' : 'Upload Audio'}
          </button>
          {audioUrl && (
            <button className="btn btn-outline" onClick={handleAudioRemove} style={{ display: 'flex', alignItems: 'center', gap: '6px', fontSize: '13px', color: '#EF4444', borderColor: '#EF4444' }}>
              <VolumeX size={14} /> Remove
            </button>
          )}
          <input ref={audioInputRef} type="file" accept="audio/*" style={{ display: 'none' }} onChange={handleAudioUpload} />
        </div>
      </div>

      {/* Stats Cards */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(130px, 1fr))', gap: '12px', marginBottom: '24px' }}>
        {[
          { label: t('invitations.total'), value: stats.total || 0, color: '#6B7280', icon: <Mail size={18} /> },
          { label: t('invitations.sent'), value: stats.sent || 0, color: '#3B82F6', icon: <Send size={18} /> },
          { label: t('invitations.drafts'), value: stats.draft || 0, color: '#9CA3AF', icon: <Clock size={18} /> },
          { label: t('invitations.accepted'), value: stats.accepted || 0, color: '#22C55E', icon: <CheckCircle size={18} /> },
          { label: t('invitations.declined'), value: stats.declined || 0, color: '#EF4444', icon: <XCircle size={18} /> },
          { label: t('invitations.attending'), value: stats.total_attending || 0, color: '#be185d', icon: <Users size={18} /> },
          // Only shown when something failed, so it can't be missed
          ...(Number(stats.failed) > 0 ? [{ label: t('invitations.failed'), value: stats.failed, color: '#EF4444', icon: <XCircle size={18} /> }] : []),
        ].map(s => (
          <div key={s.label} style={{
            background: '#fff', borderRadius: '12px', padding: '16px', textAlign: 'center',
            border: '1px solid #e5e7eb', boxShadow: '0 1px 3px rgba(0,0,0,0.04)',
          }}>
            <div style={{ color: s.color, marginBottom: '6px' }}>{s.icon}</div>
            <div style={{ fontSize: '24px', fontWeight: 700, color: s.color }}>{s.value}</div>
            <div style={{ fontSize: '12px', color: '#6b7280', marginTop: '2px' }}>{s.label}</div>
          </div>
        ))}
      </div>

      {/* Add Single Invitation Form */}
      {showAddForm && (
        <div className="detail-card" style={{ marginBottom: '20px', padding: '20px' }}>
          <h3 style={{ margin: '0 0 16px', display: 'flex', alignItems: 'center', gap: '8px' }}>
            <Plus size={18} /> {t('invitations.createNew')}
          </h3>
          <form onSubmit={handleCreateInvite}>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px' }}>
              <div>
                <label style={{ display: 'block', fontSize: '13px', fontWeight: 600, color: '#374151', marginBottom: '4px' }}>{t('invitations.recipientName')}</label>
                <input type="text" placeholder={t('invitations.guestNamePlaceholder')} value={newInvite.recipient_name}
                  onChange={e => setNewInvite({ ...newInvite, recipient_name: e.target.value })}
                  style={{ width: '100%', padding: '10px 12px', border: '1px solid #d1d5db', borderRadius: '8px', fontSize: '14px' }} required />
              </div>
              <div>
                <label style={{ display: 'block', fontSize: '13px', fontWeight: 600, color: '#374151', marginBottom: '4px' }}>{t('invitations.emailAddress')}</label>
                <input type="email" placeholder={t('invitations.guestEmailPlaceholder')} value={newInvite.recipient_email}
                  onChange={e => setNewInvite({ ...newInvite, recipient_email: e.target.value })}
                  style={{ width: '100%', padding: '10px 12px', border: '1px solid #d1d5db', borderRadius: '8px', fontSize: '14px' }} required />
              </div>
              <div>
                <label style={{ display: 'block', fontSize: '13px', fontWeight: 600, color: '#374151', marginBottom: '4px' }}>Phone Number <span style={{ fontWeight: 400, color: '#9ca3af' }}>(optional)</span></label>
                <input type="tel" placeholder="e.g. +9779812345678" value={newInvite.recipient_phone}
                  onChange={e => setNewInvite({ ...newInvite, recipient_phone: e.target.value })}
                  style={{ width: '100%', padding: '10px 12px', border: '1px solid #d1d5db', borderRadius: '8px', fontSize: '14px' }} />
              </div>
            </div>
            <div style={{ marginTop: '10px', display: 'flex', alignItems: 'flex-start', gap: '8px', background: '#f0f9ff', border: '1px solid #bae6fd', borderRadius: '8px', padding: '10px 14px' }}>
              <span style={{ fontSize: '16px', lineHeight: 1, marginTop: '1px' }}>📱</span>
              <p style={{ margin: 0, fontSize: '12px', color: '#0369a1', lineHeight: '1.5' }}>
                {/* SMS depends on an admin toggle that is off by default, so don't promise it */}
                {t('invitations.smsNote')}
              </p>
            </div>
            <div style={{ marginTop: '12px' }}>
              <label style={{ display: 'block', fontSize: '13px', fontWeight: 600, color: '#374151', marginBottom: '4px' }}>{t('invitations.personalMessage')}</label>
              <textarea placeholder={t('invitations.personalMessagePlaceholder')} value={newInvite.message}
                onChange={e => setNewInvite({ ...newInvite, message: e.target.value })}
                style={{ width: '100%', padding: '10px 12px', border: '1px solid #d1d5db', borderRadius: '8px', fontSize: '14px', minHeight: '80px', resize: 'vertical' }} />
            </div>
            <div style={{ marginTop: '12px' }}>
              <label style={{ display: 'block', fontSize: '13px', fontWeight: 600, color: '#374151', marginBottom: '4px' }}>Group</label>
              <select value={newInvite.group_name} onChange={e => setNewInvite({ ...newInvite, group_name: e.target.value })}
                style={{ width: '100%', padding: '10px 12px', border: '1px solid #d1d5db', borderRadius: '8px', fontSize: '14px', background: '#fff' }}>
                <option value="General">General</option>
                <option value="Family - Groom">Family - Groom</option>
                <option value="Family - Bride">Family - Bride</option>
                <option value="Friends - Groom">Friends - Groom</option>
                <option value="Friends - Bride">Friends - Bride</option>
                <option value="College Friends">College Friends</option>
                <option value="Work Colleagues">Work Colleagues</option>
                <option value="Neighbours">Neighbours</option>
                <option value="Couple Website">Couple Website</option>
              </select>
            </div>
            <div style={{ marginTop: '16px', display: 'flex', gap: '8px' }}>
              <button type="submit" className="btn btn-primary">{t('invitations.createInvitation')}</button>
              <button type="button" className="btn btn-outline" onClick={() => setShowAddForm(false)}>{t('common.cancel')}</button>
            </div>
          </form>
        </div>
      )}

      {/* Bulk Import from Guest List */}
      {showBulkImport && (
        <div className="detail-card" style={{ marginBottom: '20px', padding: '20px' }}>
          <h3 style={{ margin: '0 0 16px', display: 'flex', alignItems: 'center', gap: '8px' }}>
            <UserPlus size={18} /> {t('invitations.importTitle')}
          </h3>
          {/* Guests without an email are left out of the list below; say how many and why */}
          {guestsWithoutEmail > 0 && (
            <p style={{ color: '#92400e', background: '#fffbeb', border: '1px solid #fde68a', borderRadius: '8px', padding: '8px 12px', fontSize: '13px', margin: '0 0 12px' }}>
              {t('invitations.noEmailGuests').replace('{count}', guestsWithoutEmail)} <Link to="/guests" style={{ color: '#be185d' }}>{t('invitations.guestListLink')}</Link>.
            </p>
          )}
          {guests.filter(g => g.email).length === 0 ? (
            <p style={{ color: '#6b7280' }}>{t('invitations.noGuestsWithEmail')} <Link to="/guests" style={{ color: '#be185d' }}>{t('invitations.addGuestsFirst')}</Link>.</p>
          ) : (
            <>
              <div style={{ display: 'flex', alignItems: 'center', gap: '16px', marginBottom: '12px', flexWrap: 'wrap' }}>
                <label style={{ display: 'flex', alignItems: 'center', gap: '8px', cursor: 'pointer', fontSize: '13px', fontWeight: 600, color: '#374151' }}>
                  <input type="checkbox" checked={selectedGuests.length > 0 && selectedGuests.length === guests.filter(g => g.email && !findInvitation(g.email) && (filterBulkGroup === 'all' || g.group_name === filterBulkGroup)).length}
                    onChange={selectAllGuests} /> {t('invitations.selectAllEligible')}
                </label>
                <select
                  value={filterBulkGroup}
                  onChange={e => { setFilterBulkGroup(e.target.value); setSelectedGuests([]); }}
                  style={{ padding: '6px 10px', border: '1px solid #d1d5db', borderRadius: '8px', fontSize: '13px', color: '#374151' }}
                >
                  <option value="all">All Groups</option>
                  <option value="General">General</option>
                  <option value="Family - Groom">Family - Groom</option>
                  <option value="Family - Bride">Family - Bride</option>
                  <option value="Friends - Groom">Friends - Groom</option>
                  <option value="Friends - Bride">Friends - Bride</option>
                  <option value="College Friends">College Friends</option>
                  <option value="Work Colleagues">Work Colleagues</option>
                  <option value="Neighbours">Neighbours</option>
                  <option value="Couple Website">Couple Website</option>
                </select>
              </div>
              <div style={{ maxHeight: '300px', overflowY: 'auto', border: '1px solid #e5e7eb', borderRadius: '8px' }}>
                {guests.filter(g => g.email && (filterBulkGroup === 'all' || g.group_name === filterBulkGroup)).map(guest => {
                  const alreadyInvited = findInvitation(guest.email);
                  return (
                    <div key={guest.id} style={{
                      display: 'flex', alignItems: 'center', gap: '12px', padding: '10px 14px',
                      borderBottom: '1px solid #f3f4f6', opacity: alreadyInvited ? 0.5 : 1,
                    }}>
                      <input type="checkbox" disabled={!!alreadyInvited}
                        checked={selectedGuests.includes(guest.id)}
                        onChange={() => toggleGuestSelect(guest.id)} />
                      <div style={{ flex: 1 }}>
                        <div style={{ fontSize: '14px', fontWeight: 500 }}>{guest.first_name} {guest.last_name}</div>
                        <div style={{ fontSize: '12px', color: '#6b7280' }}>{guest.email} {guest.group_name ? `• ${guest.group_name}` : ''}</div>
                      </div>
                      {alreadyInvited && <span style={{ fontSize: '11px', color: '#9ca3af' }}>{t('invitations.alreadyInvited')}</span>}
                    </div>
                  );
                })}
              </div>
              <div style={{ marginTop: '12px' }}>
                <label style={{ display: 'block', fontSize: '13px', fontWeight: 600, color: '#374151', marginBottom: '4px' }}>{t('invitations.messageForAll')}</label>
                <textarea placeholder={t('invitations.messagePlaceholder')} value={bulkMessage}
                  onChange={e => setBulkMessage(e.target.value)}
                  style={{ width: '100%', padding: '10px 12px', border: '1px solid #d1d5db', borderRadius: '8px', fontSize: '14px', minHeight: '60px', resize: 'vertical' }} />
              </div>
              <div style={{ marginTop: '16px', display: 'flex', gap: '8px', alignItems: 'center' }}>
                <button className="btn btn-primary" onClick={handleBulkImport} disabled={selectedGuests.length === 0}>
                  Import {selectedGuests.length} Guest{selectedGuests.length !== 1 ? 's' : ''}
                </button>
                <button className="btn btn-outline" onClick={() => { setShowBulkImport(false); setSelectedGuests([]); }}>{t('common.cancel')}</button>
              </div>
            </>
          )}
        </div>
      )}

      {/* Card Designer */}
      {showCardEditor && (
        <div className="detail-card" style={{ marginBottom: '20px', padding: '24px' }}>
          <h3 style={{ margin: '0 0 16px', display: 'flex', alignItems: 'center', gap: '8px' }}>
            <Palette size={18} /> {editingCardId ? t('invitations.editCardDesign') : t('invitations.designInvitationCard')}
          </h3>
          <InvitationCardEditor
            template={editingCardId ? (invitations.find(i => i.id === editingCardId)?.card_template || 'classic') : 'classic'}
            cardData={editingCardId ? (invitations.find(i => i.id === editingCardId)?.card_data || {}) : {}}
            coupleNames={`${wedding.partner1_name} & ${wedding.partner2_name}`}
            weddingDate={wedding.wedding_date}
            venue={[wedding.venue_location, wedding.city].filter(Boolean).join(', ')}
            onSave={async (template, cardData) => {
              try {
                if (editingCardId) {
                  await updateInvitation(editingCardId, { card_template: template, card_data: cardData });
                } else {
                  // Apply to all draft invitations
                  const drafts = invitations.filter(i => i.email_status === 'draft');
                  // Designs are stored per invitation, with no wedding-level default,
                  // so with no drafts nothing would be saved: keep the editor open.
                  if (drafts.length === 0) {
                    toast.warn(t('invitations.noDraftsForCard'));
                    return;
                  }
                  for (const inv of drafts) {
                    await updateInvitation(inv.id, { card_template: template, card_data: cardData });
                  }
                  toast.success(`Card design applied to ${drafts.length} draft invitation(s).`);
                }
                await loadData();
                setShowCardEditor(false);
                setEditingCardId(null);
              } catch (err) {
                toast.error(err.response?.data?.error || 'Failed to save card design');
              }
            }}
            onCancel={() => { setShowCardEditor(false); setEditingCardId(null); }}
          />
        </div>
      )}

      {/* Reminder Settings */}
      {invitations.filter(i => i.email_status === 'sent' && i.rsvp_status === 'pending').length > 0 && (
        <div className="detail-card" style={{ padding: '16px 20px', marginBottom: '20px', display: 'flex', alignItems: 'center', flexWrap: 'wrap', gap: '16px', background: '#faf5ff', border: '1px solid #e9d5ff' }}>
          <Bell size={20} style={{ color: '#7c3aed' }} />
          <div style={{ flex: 1 }}>
            <div style={{ fontWeight: 600, color: '#374151', fontSize: '14px' }}>{t('invitations.autoReminders')}</div>
            <div style={{ fontSize: '12px', color: '#6b7280', marginTop: '2px' }}>
              Automatically remind {invitations.filter(i => i.email_status === 'sent' && i.rsvp_status === 'pending').length} guest(s) who haven't responded yet
            </div>
          </div>
          <select
            value={(() => {
              const pending = invitations.filter(i => i.email_status === 'sent' && i.rsvp_status === 'pending');
              const freqs = pending.map(i => i.reminder_frequency || 'none');
              return freqs.every(f => f === freqs[0]) ? freqs[0] : '';
            })()}
            onChange={async (e) => {
              try {
                await updateBulkReminderFrequency(wedding.id, e.target.value);
                toast.success('Reminder schedule updated');
                await loadData();
              } catch (err) {
                toast.error(err.response?.data?.error || 'Failed to update reminders');
              }
            }}
            style={{ padding: '8px 14px', border: '1px solid #d1d5db', borderRadius: '8px', fontSize: '13px', fontWeight: 500, cursor: 'pointer' }}
          >
            <option value="">Mixed...</option>
            <option value="none">{t('invitations.off')}</option>
            <option value="weekly">{t('invitations.weekly')}</option>
            <option value="biweekly">{t('invitations.every2Weeks')}</option>
            <option value="monthly">{t('invitations.monthly')}</option>
          </select>
        </div>
      )}

      {/* Filter Tabs & Send All */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: '12px', marginBottom: '16px' }}>
        <div style={{ display: 'flex', gap: '6px', flexWrap: 'wrap' }}>
          {[
            { key: 'all', label: t('invitations.all'), count: invitations.length },
            { key: 'draft', label: t('invitations.drafts'), count: invitations.filter(i => i.email_status === 'draft').length },
            { key: 'sent', label: t('invitations.sent'), count: invitations.filter(i => i.email_status === 'sent' || i.email_status === 'delivered').length },
            { key: 'accepted', label: t('invitations.accepted'), count: invitations.filter(i => i.rsvp_status === 'accepted').length },
            { key: 'declined', label: t('invitations.declined'), count: invitations.filter(i => i.rsvp_status === 'declined').length },
            { key: 'pending', label: t('invitations.awaitingRsvp'), count: invitations.filter(i => i.rsvp_status === 'pending' && wasSent(i)).length },
            // The filter already handled 'failed' but no tab ever selected it
            ...(failedCount > 0 ? [{ key: 'failed', label: t('invitations.failed'), count: failedCount }] : []),
          ].map(tab => (
            <button key={tab.key} onClick={() => setFilterStatus(tab.key)}
              style={{
                padding: '6px 14px', borderRadius: '999px', fontSize: '13px', fontWeight: 500, cursor: 'pointer',
                border: filterStatus === tab.key ? '2px solid #be185d' : '1px solid #d1d5db',
                background: filterStatus === tab.key ? '#fdf2f8' : '#fff',
                color: filterStatus === tab.key ? '#be185d' : '#6b7280',
              }}>
              {tab.label} ({tab.count})
            </button>
          ))}
        </div>
        {(stats.draft || 0) > 0 && (
          <button className="btn btn-primary" onClick={handleSendAllDrafts} disabled={sending.bulk}
            style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
            <Send size={14} /> {sending.bulk ? t('common.sending') : `${t('invitations.sendAllDrafts')} (${stats.draft})`}
          </button>
        )}
        {failedCount > 0 && (
          <button className="btn btn-outline" onClick={handleRetryFailed} disabled={sending.retry}
            style={{ display: 'flex', alignItems: 'center', gap: '6px', color: '#EF4444', borderColor: '#EF4444' }}>
            <RefreshCw size={14} /> {sending.retry ? t('common.sending') : `${t('invitations.retryFailed')} (${failedCount})`}
          </button>
        )}
      </div>

      {/* Invitations List */}
      {filteredInvitations.length === 0 ? (
        <div style={{ textAlign: 'center', padding: '60px 20px', background: '#f9fafb', borderRadius: '12px', border: '1px dashed #d1d5db' }}>
          <Mail size={48} style={{ color: '#d1d5db', marginBottom: '12px' }} />
          <h3 style={{ color: '#374151', marginBottom: '8px' }}>
            {invitations.length === 0 ? t('invitations.noInvitationsYet') : t('invitations.noInvitationsMatch')}
          </h3>
          <p style={{ color: '#6b7280', fontSize: '14px' }}>
            {invitations.length === 0 ? t('invitations.createFirst') : t('invitations.tryDifferentFilter')}
          </p>
        </div>
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
          {filteredInvitations.map(inv => (
            <div key={inv.id} className="detail-card" style={{
              padding: '16px 20px', display: 'flex', alignItems: 'center', flexWrap: 'wrap', gap: '16px',
              borderLeft: inv.rsvp_status === 'accepted' ? '4px solid #22C55E' : inv.rsvp_status === 'declined' ? '4px solid #EF4444' : inv.email_status === 'draft' ? '4px solid #9CA3AF' : '4px solid #3B82F6',
            }}>
              {/* Main Info */}
              <div style={{ flex: 1, minWidth: '200px' }}>
                {editingId === inv.id ? (
                  <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
                    <input value={editForm.recipient_name || ''} onChange={e => setEditForm({ ...editForm, recipient_name: e.target.value })}
                      style={{ padding: '6px 10px', border: '1px solid #d1d5db', borderRadius: '6px', fontSize: '13px' }} placeholder="Name" />
                    <input value={editForm.recipient_email || ''} onChange={e => setEditForm({ ...editForm, recipient_email: e.target.value })}
                      style={{ padding: '6px 10px', border: '1px solid #d1d5db', borderRadius: '6px', fontSize: '13px' }} placeholder="Email" />
                    <input value={editForm.recipient_phone || ''} onChange={e => setEditForm({ ...editForm, recipient_phone: e.target.value })}
                      style={{ padding: '6px 10px', border: '1px solid #d1d5db', borderRadius: '6px', fontSize: '13px', width: '150px' }} placeholder="Phone (optional)" />
                    <button className="btn btn-primary" style={{ padding: '6px 12px', fontSize: '12px' }} onClick={() => handleEditSave(inv.id)}>Save</button>
                    <button className="btn btn-outline" style={{ padding: '6px 12px', fontSize: '12px' }} onClick={() => setEditingId(null)}>{t('common.cancel')}</button>
                  </div>
                ) : (
                  <>
                    <div style={{ fontSize: '15px', fontWeight: 600, color: '#1f2937' }}>{inv.recipient_name}</div>
                    <div style={{ fontSize: '13px', color: '#6b7280' }}>{inv.recipient_email}{inv.recipient_phone && <span style={{ marginLeft: '8px', color: '#9ca3af' }}>· 📱 {inv.recipient_phone}</span>}</div>
                    {inv.message && <div style={{ fontSize: '12px', color: '#9ca3af', marginTop: '4px', fontStyle: 'italic' }}>"{inv.message.substring(0, 80)}{inv.message.length > 80 ? '...' : ''}"</div>}
                  </>
                )}
              </div>

              {/* Statuses */}
              <div style={{ display: 'flex', gap: '8px', alignItems: 'center', flexWrap: 'wrap' }}>
                {emailBadge(inv.email_status)}
                {/* A failed invitation that never went out isn't "awaiting reply" */}
                {(wasSent(inv) || inv.rsvp_status !== 'pending') && rsvpBadge(inv.rsvp_status)}
                {inv.rsvp_guest_count > 1 && inv.rsvp_status === 'accepted' && (
                  <span style={{ fontSize: '11px', color: '#6b7280' }}>+{inv.rsvp_guest_count - 1} guest{inv.rsvp_guest_count > 2 ? 's' : ''}</span>
                )}
                {inv.opened_at && (
                  <span title={`Opened: ${new Date(inv.opened_at).toLocaleString()}`} style={{ color: '#3B82F6' }}>
                    <Eye size={14} />
                  </span>
                )}
              </div>

              {/* Actions */}
              <div style={{ display: 'flex', gap: '6px', flexShrink: 0 }}>
                {inv.email_status === 'draft' && (
                  <button onClick={() => handleSend(inv.id)} disabled={sending[inv.id]}
                    title="Send Invitation"
                    style={{ padding: '6px 12px', background: '#be185d', color: '#fff', border: 'none', borderRadius: '6px', fontSize: '12px', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: '4px' }}>
                    <Send size={12} /> {sending[inv.id] ? '...' : 'Send'}
                  </button>
                )}
                {inv.email_status !== 'draft' && (
                  <button onClick={() => handleResend(inv.id)} disabled={sending[inv.id]}
                    title="Resend"
                    style={{ padding: '6px 12px', background: '#3B82F6', color: '#fff', border: 'none', borderRadius: '6px', fontSize: '12px', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: '4px' }}>
                    <RefreshCw size={12} /> {sending[inv.id] ? '...' : t('invitations.resend')}
                  </button>
                )}
                {/* Failed ones too: a mistyped address is the usual cause, and Resend alone can't fix it */}
                {(inv.email_status === 'draft' || inv.email_status === 'failed') && (
                  <button onClick={() => { setEditingId(inv.id); setEditForm({ recipient_name: inv.recipient_name, recipient_email: inv.recipient_email, recipient_phone: inv.recipient_phone || '', message: inv.message }); }}
                    title="Edit"
                    style={{ padding: '6px 10px', background: '#f3f4f6', color: '#374151', border: '1px solid #d1d5db', borderRadius: '6px', fontSize: '12px', cursor: 'pointer' }}>
                    {t('common.edit')}
                  </button>
                )}
                <button onClick={() => handleDelete(inv)} title="Delete"
                  style={{ padding: '6px 10px', background: '#FEF2F2', color: '#EF4444', border: '1px solid #FECACA', borderRadius: '6px', fontSize: '12px', cursor: 'pointer' }}>
                  <Trash2 size={12} />
                </button>
              </div>

              {/* RSVP Note */}
              {inv.rsvp_note && (
                <div style={{ width: '100%', fontSize: '12px', color: '#6b7280', background: '#f9fafb', padding: '8px 12px', borderRadius: '6px', marginTop: '4px' }}>
                  <strong>RSVP Note:</strong> {inv.rsvp_note}
                  {inv.rsvp_responded_at && <span style={{ marginLeft: '8px', color: '#9ca3af' }}>• {new Date(inv.rsvp_responded_at).toLocaleDateString()}</span>}
                </div>
              )}
              {/* Meal & Kids */}
              {inv.rsvp_status === 'accepted' && (inv.meal_preference || inv.rsvp_kids_count > 0) && (
                <div style={{ width: '100%', display: 'flex', gap: '10px', flexWrap: 'wrap', marginTop: '4px' }}>
                  {inv.meal_preference && (
                    <span title="Guest meal preference" style={{ fontSize: '11px', padding: '2px 10px', borderRadius: '999px', background: inv.meal_preference === 'veg' ? '#F0FDF420' : '#FFF7ED', color: inv.meal_preference === 'veg' ? '#15803d' : '#c2410c', border: `1px solid ${inv.meal_preference === 'veg' ? '#22C55E40' : '#F9731640'}`, fontWeight: 600 }}>
                      {inv.meal_preference === 'veg' ? '🥗 Veg' : '🍗 Non-Veg'}
                    </span>
                  )}
                  {inv.rsvp_kids_count > 0 && (
                    <span title="Number of kids attending" style={{ fontSize: '11px', padding: '2px 10px', borderRadius: '999px', background: '#EFF6FF', color: '#1d4ed8', border: '1px solid #BFDBFE', fontWeight: 600 }}>
                      👶 {inv.rsvp_kids_count} kid{inv.rsvp_kids_count > 1 ? 's' : ''}
                    </span>
                  )}
                </div>
              )}
            </div>
          ))}
        </div>
      )}
      {ConfirmDialog}
    </div>
  );
}

export default Invitations;
